import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer({ onContactClick }) {
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/company-info`);
      // Add cache busting to logo URL
      const data = response.data;
      if (data.logo_url && !data.logo_url.includes('?v=')) {
        data.logo_url = `${data.logo_url}?v=${Date.now()}`;
      }
      setCompanyInfo(data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const services = [
    'IT Trainings',
    'Placements',
    'Computer Sales',
    'Networking Solutions',
    'Email Setup',
  ];

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Courses', path: '/courses' },
    { label: 'Why Choose Us', path: '/why-choose-us' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            {companyInfo?.logo_url ? (
              <img 
                src={companyInfo.logo_url} 
                alt={companyInfo.company_name}
                className="h-12 w-auto object-contain mb-6 bg-white p-2 rounded"
              />
            ) : (
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-sky-500 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">S</span>
                </div>
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  SKANDA IT
                </span>
              </div>
            )}
            <p className="text-slate-300 mb-6 leading-relaxed">
              Your trusted partner for IT trainings, placements, and comprehensive technology solutions.
            </p>
            <div className="flex space-x-4">
              <a href={companyInfo?.social_links?.facebook || "#"} data-testid="footer-social-facebook" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-all">
                <Facebook size={18} className="text-slate-300" />
              </a>
              <a href={companyInfo?.social_links?.twitter || "#"} data-testid="footer-social-twitter" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-blue-400 transition-all">
                <Twitter size={18} className="text-slate-300" />
              </a>
              <a href={companyInfo?.social_links?.linkedin || "#"} data-testid="footer-social-linkedin" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-blue-700 transition-all">
                <Linkedin size={18} className="text-slate-300" />
              </a>
              <a href={companyInfo?.social_links?.instagram || "#"} data-testid="footer-social-instagram" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition-all">
                <Instagram size={18} className="text-slate-300" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${index}`}
                    className="text-slate-300 hover:text-blue-400 transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${index}`}
                    className="text-slate-300 hover:text-blue-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              {/* Contact link - opens modal */}
              <li>
                <button
                  onClick={onContactClick}
                  data-testid="footer-contact-btn"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-left"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                <span className="text-slate-300">
                  {companyInfo?.address || 'Loading...'}
                </span>
              </li>
              {companyInfo?.phone && companyInfo.phone.map((phone, idx) => (
                <li key={idx} className="flex items-center space-x-3">
                  <Phone size={20} className="text-blue-400 flex-shrink-0" />
                  <a href={`tel:${phone}`} data-testid={`footer-phone-${idx}`} className="text-slate-300 hover:text-blue-400 transition-colors">
                    {phone}
                  </a>
                </li>
              ))}
              {companyInfo?.email && companyInfo.email.map((email, idx) => (
                <li key={idx} className="flex items-center space-x-3">
                  <Mail size={20} className="text-blue-400 flex-shrink-0" />
                  <a href={`mailto:${email}`} data-testid={`footer-email-${idx}`} className="text-slate-300 hover:text-blue-400 transition-colors">
                    {email}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 mt-12 pt-8 text-center text-slate-400">
          <p>&copy; {new Date().getFullYear()} {companyInfo?.company_name || 'SKANDA IT SOLUTIONS'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
